import { addSlideFooter } from '../slide_footer';
import { FONT_FACE, LINK_COLOR, TITLE_TEXT } from '../constants';

const CONTENT_BOTTOM = 5.0;
const CONTENT_TOP = 1.2;
const CONTENT_WIDTH = 9.2;
const CONTENT_X = 0.5;
const INDENT = 0.34;
const LINE_H = 0.19;
const PARAGRAPH_GAP = 0.06;

const NAMED_ENTITIES = {
  amp: '&',
  apos: "'",
  gt: '>',
  lt: '<',
  nbsp: ' ',
  quot: '"',
};

const decodeHtmlEntities = (text) =>
  text
    .replace(/&#x([0-9a-f]+);/gi, (_match, codePoint) =>
      String.fromCodePoint(parseInt(codePoint, 16))
    )
    .replace(/&#(\d+);/g, (_match, codePoint) => String.fromCodePoint(parseInt(codePoint, 10)))
    .replace(/&([a-z]+);/gi, (match, name) => NAMED_ENTITIES[name.toLowerCase()] || match);

const toParagraphs = (body) =>
  decodeHtmlEntities(body)
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => line.trimEnd())
    .filter((line) => line.trim())
    .map((line) => {
      const match = line.match(/^(\s*)(?:[-*+]|•)\s+(.*)$/);
      if (!match) return { level: 0, text: line.trim(), type: 'paragraph' };
      const indentation = match[1].replace(/\t/g, '  ').length;
      return { level: Math.floor(indentation / 2), text: match[2].trim(), type: 'bullet' };
    });

const getParagraphHeight = ({ level, text }) => {
  const charactersPerLine = Math.max(35, 92 - level * 12);
  return Math.max(1, Math.ceil(text.length / charactersPerLine)) * LINE_H + PARAGRAPH_GAP;
};

const paginateParagraphs = (paragraphs) =>
  paragraphs.reduce(
    (pages, paragraph) => {
      const page = pages[pages.length - 1];
      const usedHeight = page.reduce((total, item) => total + getParagraphHeight(item), 0);
      if (
        page.length &&
        usedHeight + getParagraphHeight(paragraph) > CONTENT_BOTTOM - CONTENT_TOP
      ) {
        pages.push([paragraph]);
      } else {
        page.push(paragraph);
      }
      return pages;
    },
    [[]]
  );

/**
 * Creates one or more Key Takeaways slides from plain text or Markdown-style bullet lists.
 * HTML entities are decoded so the export matches ReactMarkdown's browser rendering.
 */
export const createTakeawaysSlide = ({
  body,
  pptx,
  reportDateRange,
  reportType,
  reportUrl,
  shopperAttributes,
  slideTitle = 'Key Takeaways',
}) => {
  const pages = paginateParagraphs(toParagraphs(body));

  return pages.map((page, pageIndex) => {
    const slide = pptx.addSlide();
    const title = pageIndex
      ? `${slideTitle} (continued ${pageIndex + 1}/${pages.length})`
      : slideTitle;
    slide.addText(title, { ...TITLE_TEXT, fontSize: 20 });

    let y = CONTENT_TOP;
    page.forEach((paragraph) => {
      const h = getParagraphHeight(paragraph);
      const x = CONTENT_X + (paragraph.type === 'bullet' ? paragraph.level * INDENT : 0);
      const w = CONTENT_WIDTH - (paragraph.type === 'bullet' ? paragraph.level * INDENT : 0);
      slide.addText(paragraph.type === 'bullet' ? `• ${paragraph.text}` : paragraph.text, {
        align: 'left',
        breakLine: true,
        color: LINK_COLOR,
        fit: 'shrink',
        fontFace: FONT_FACE,
        fontSize: 10,
        h,
        margin: 0,
        valign: 'top',
        w,
        x,
        y,
      });
      y += h;
    });

    addSlideFooter({ pptx, reportDateRange, reportType, reportUrl, shopperAttributes, slide });
    return slide;
  })[0];
};
